/**
 * Support-session data for the Kin ticket chip (PS-1358).
 *
 * Two sources carry the same session object, so the chip reads both through one shape:
 * - ``GET /v2/chat/threads/{id}/messages`` returns ``support_sessions`` (oldest first) on load,
 * - ``POST /v2/chat/stream`` emits a ``data-kin-support-session`` part on every HUMAN-mode turn.
 *
 * The agent mirrors DevRev's assignee and stage onto the session, so a later payload simply wins:
 * there is no client-side merge of individual fields.
 */

import type { UIMessagePartsLike } from './data-suggestions';

const SESSION_PART_TYPE = 'data-kin-support-session';

/** States the agent writes on ``chat_support_sessions.state``. */
export const SUPPORT_SESSION_ACTIVE_STATES = ['REQUESTED', 'OPEN'] as const;

/** One support session as serialized by the agent (``support_session_row_to_api_dict``). */
export type SupportSessionData = {
  id: string;
  state: string;
  /** DevRev ``display_id`` of the ticket, e.g. ``TKT-456``. */
  ticketDisplayId: string;
  /** DevRev owner display name; null while the ticket is unassigned ("if applicable" in the PRD). */
  assigneeName: string | null;
  /** Raw DevRev stage name (``queued``, ``in_development``, …); mapped for display, never shown raw. */
  ticketStatus: string | null;
  openedAt: string | null;
  closedAt: string | null;
  /**
   * The assistant message that carried the handoff offer, when the session began from one.
   * Null when accepted via the persistent control, which has no offer to anchor to.
   */
  offerMessageId: string | null;
};

type SupportSessionJson = {
  id?: unknown;
  state?: unknown;
  ticket_display_id?: unknown;
  assignee_name?: unknown;
  ticket_status?: unknown;
  opened_at?: unknown;
  closed_at?: unknown;
  offer_message_id?: unknown;
};

function optionalString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

/** Parse one serialized session; returns undefined when the object is not a usable session. */
export function parseSupportSession(raw: unknown): SupportSessionData | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const json = raw as SupportSessionJson;
  const id = optionalString(json.id);
  const state = optionalString(json.state);
  const ticketDisplayId = optionalString(json.ticket_display_id);
  if (!id || !state || !ticketDisplayId) return undefined;
  return {
    id,
    state,
    ticketDisplayId,
    assigneeName: optionalString(json.assignee_name),
    ticketStatus: optionalString(json.ticket_status),
    openedAt: optionalString(json.opened_at),
    closedAt: optionalString(json.closed_at),
    offerMessageId: optionalString(json.offer_message_id),
  };
}

/** Parse the ``support_sessions`` array from the thread messages payload, dropping bad entries. */
export function parseSupportSessions(raw: unknown): SupportSessionData[] {
  if (!Array.isArray(raw)) return [];
  const out: SupportSessionData[] = [];
  for (const entry of raw) {
    const session = parseSupportSession(entry);
    if (session) out.push(session);
  }
  return out;
}

export function isSupportSessionActive(session: SupportSessionData): boolean {
  return (SUPPORT_SESSION_ACTIVE_STATES as readonly string[]).includes(session.state);
}

/** The live session for a thread, if any. Sessions are sequential, so at most one is active. */
export function activeSupportSession(
  sessions: readonly SupportSessionData[]
): SupportSessionData | undefined {
  for (let i = sessions.length - 1; i >= 0; i -= 1) {
    const session = sessions[i];
    if (isSupportSessionActive(session)) return session;
  }
  return undefined;
}

/**
 * Newest session carried by a ``data-kin-support-session`` part, scanning from the last message.
 * The stream part is fresher than the payload fetched when the thread was opened.
 */
export function latestStreamedSupportSession(
  messages: readonly UIMessagePartsLike[]
): SupportSessionData | undefined {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const parts = messages[i]?.parts;
    if (!parts?.length) continue;
    for (let j = parts.length - 1; j >= 0; j -= 1) {
      const part = parts[j];
      if (part.type !== SESSION_PART_TYPE) continue;
      const data = part.data as { session?: unknown } | undefined;
      const session = parseSupportSession(data?.session);
      if (session) return session;
    }
  }
  return undefined;
}

/**
 * Every session to draw on the thread, oldest first.
 *
 * A thread can hand off more than once: the first ticket resolves, the customer asks for support
 * again, and a second session opens. Both belong in the transcript at their own handover points,
 * so this returns the whole list rather than "the current one" - picking a single session erases
 * the earlier ticket's markers from a conversation the customer can still scroll through.
 *
 * The two sources disagree per session and each can be behind the other:
 * - the streamed part is emitted only by ``human_mode_sse_lines``, i.e. only while the thread is
 *   in HUMAN mode, so it can never report the close - and it is deliberately kept across poll
 *   refreshes (``EPHEMERAL_UI_PART_TYPES``), so it lingers as an active-looking session long
 *   after the ticket resolved;
 * - the loaded list is the server's own view of every session on the thread, so it is the only
 *   source that ever reports a resolution.
 *
 * So the loaded row wins whenever it holds the session. It is refetched every 10s while a session
 * is live and again each time a stream settles, so it is never older than the streamed copy for
 * longer than one round trip. The streamed part only fills in a session the loaded list has not
 * caught up with yet. Neither payload carries a modified timestamp, so letting the streamed row
 * win "while live" cannot tell a fresh part from one kept across polls, and it pins a stale
 * assignee and status for the whole idle wait.
 */
export function supportSessionsForDisplay(
  streamedSession: SupportSessionData | undefined,
  loadedSessions: readonly SupportSessionData[]
): SupportSessionData[] {
  const sessions = loadedSessions.slice();
  if (!streamedSession) return sessions;
  if (sessions.some(session => session.id === streamedSession.id)) return sessions;
  // Accepted this turn; the thread payload has not been refetched yet.
  sessions.push(streamedSession);
  return sessions;
}

/**
 * Add a just-accepted session to the held list unless a poll already inserted it.
 *
 * Appends rather than reusing ``mergeSupportSessionSnapshot``, which lists the snapshot first and
 * would put a newer session ahead of older ones; the transcript draws sessions oldest first. A
 * session already held is left alone: it can only have arrived from a fetch that ran after the
 * accept committed, so it is at least as fresh as the accept response.
 */
export function seedSupportSession(
  held: readonly SupportSessionData[],
  session: SupportSessionData
): SupportSessionData[] {
  if (held.some(existing => existing.id === session.id)) return held.slice();
  return [...held, session];
}

/**
 * Fold a fetched ``support_sessions`` snapshot into the sessions the panel already holds.
 *
 * The snapshot wins for every session it lists - it is the only source that reports a close. A
 * held session it omits is kept: sessions are append-only, so a missing one is newer than the
 * snapshot, typically seeded by an accept that landed while this fetch was in flight. Replacing
 * outright would erase that ticket's chrome until the next poll.
 */
export function mergeSupportSessionSnapshot(
  held: readonly SupportSessionData[],
  snapshot: readonly SupportSessionData[]
): SupportSessionData[] {
  const listed = new Set(snapshot.map(session => session.id));
  return [...snapshot, ...held.filter(session => !listed.has(session.id))];
}

/**
 * Index of the message the resolved marker follows: the end of the stretch the human agent owned.
 *
 * Kin stays silent while the thread is in HUMAN mode - ``human_mode_sse_lines`` emits a message
 * with no assistant text - so the first assistant message *with text* after the handover is the
 * resume that closes the session, and the marker belongs immediately before it. Every customer
 * message in the session leaves one of those empty assistant shells behind, so counting any
 * assistant row puts the marker above the replies it resolved. ``text`` left undefined counts as
 * text, for callers that only know roles.
 *
 * ``anchorIndex`` is where the connected chrome renders. A session with nothing after it (closed
 * before anyone replied, or a transcript that never persisted the resume) falls back to the end
 * of the thread, which is where the connected chrome also lands when it has no anchor.
 */
export function supportSessionResolvedMarkerIndex(
  messages: readonly { role: string; text?: string }[],
  anchorIndex: number
): number {
  const lastIndex = messages.length - 1;
  if (anchorIndex < 0 || anchorIndex >= lastIndex) return lastIndex;
  for (let i = anchorIndex + 1; i <= lastIndex; i += 1) {
    const { role, text } = messages[i];
    if (role === 'assistant' && (text === undefined || text.trim().length > 0)) return i - 1;
  }
  return lastIndex;
}

/**
 * DevRev's default ticket stages, mapped to the chip's wording.
 *
 * Stages are configurable per DevRev workspace, so this map cannot be exhaustive; an unmapped
 * stage is title-cased rather than shown as a raw enum (``awaiting_dev_assist`` reads as
 * "Awaiting Dev Assist"). A customer must never see a snake_case identifier.
 */
const TICKET_STATUS_LABELS: Record<string, string> = {
  queued: 'Queued',
  work_in_progress: 'In Progress',
  in_development: 'In Progress',
  awaiting_product_assist: 'Awaiting Product Assist',
  awaiting_development: 'Awaiting Development',
  awaiting_customer_response: 'Awaiting Your Response',
  resolved: 'Resolved',
  accepted: 'Resolved',
  cancelled: 'Cancelled',
  canceled: 'Cancelled',
};

function titleCase(raw: string): string {
  return raw
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/** Display label for a DevRev stage; null when there is no stage to show yet. */
export function formatTicketStatusLabel(ticketStatus: string | null | undefined): string | null {
  const raw = ticketStatus?.trim();
  if (!raw) return null;
  return TICKET_STATUS_LABELS[raw.toLowerCase()] ?? titleCase(raw);
}

/**
 * True when `messageId` is this session's acknowledgement - the message that marks the point the
 * thread changed destination, and where the session chrome belongs.
 *
 * The agent writes that message with `ui_message_id = msg_<session id hex>` precisely so the
 * client can recompute it from the session it already holds. Compared on the bare hex because
 * the id arrives as `msg_<32 hex>` on messages and as a UUID on the session.
 *
 * `offerMessageId` is deliberately not used: it points at the offer, two messages earlier, so
 * the chrome would render above the customer's own acceptance.
 */
export function supportSessionAnchorsMessage(
  session: SupportSessionData,
  messageId: string | null | undefined
): boolean {
  const normalize = (value: string | null | undefined): string | null => {
    if (!value) return null;
    const hex = value.trim().toLowerCase().replace(/^msg_/, '').replace(/-/g, '');
    return /^[0-9a-f]{32}$/.test(hex) ? hex : null;
  };
  const anchor = normalize(session.id);
  const candidate = normalize(messageId);
  return anchor !== null && anchor === candidate;
}

/** One piece of session chrome and the index of the message it renders after. */
export type SupportSessionChromeRow = {
  index: number;
  session: SupportSessionData;
  kind: 'connected' | 'resolved';
};

/**
 * Where each session's markers belong in the thread, in render order.
 *
 * The chrome is part of the transcript, not a header: a thread that handed off twice carries two
 * sets of markers, each at its own handover. Anchoring is per session (`supportSessionAnchorsMessage`
 * against the acknowledgement the agent derived from the session id), so sessions stay put as the
 * conversation grows underneath them.
 *
 * A session whose acknowledgement is not in the loaded page falls back to the end of the thread,
 * which is also where a session accepted before the acknowledgement existed lands.
 */
export function supportSessionChromeLayout(
  sessions: readonly SupportSessionData[],
  messages: readonly { id?: string | null; role: string; text?: string }[]
): SupportSessionChromeRow[] {
  const lastIndex = messages.length - 1;
  if (lastIndex < 0) return [];
  const rows: SupportSessionChromeRow[] = [];
  for (const session of sessions) {
    const anchor = messages.findIndex(message => supportSessionAnchorsMessage(session, message.id));
    const connectedAt = anchor === -1 ? lastIndex : anchor;
    rows.push({ index: connectedAt, session, kind: 'connected' });
    if (session.closedAt) {
      rows.push({
        index: supportSessionResolvedMarkerIndex(messages, connectedAt),
        session,
        kind: 'resolved',
      });
    }
  }
  return rows;
}
